/**
 * @file Client wrappers for the form point-in-time-recovery callables
 * (SYD-2450). Consumers: `useFormRecoveryList`, which puts the restore point
 * ladder and every refinement of it onto `formRecoveryStore`,
 * `useFormRecoveryRead`, which writes one row's outcome onto the same proxy,
 * and `useFormRecoveryRestore`, which records the undo entry from a restore's
 * response. Neither the group nor the banner calls here directly — they render
 * from the store the hooks write.
 *
 * No try/catch anywhere in this file. A rejection must reach its caller with its
 * `code` and `details` intact: the out-of-window refusal is an
 * `invalid-argument` whose message names the floor, and a restore's refusal is
 * `failed-precondition` carrying a `RestoreFormVersionConflict`. Wrapping either
 * in `new Error(...)` flattens both into a string.
 */

import { httpsCallable } from 'firebase/functions';
import type {
  ListFormVersionRestorePointsBetweenRequest,
  ListFormVersionRestorePointsBetweenResponse,
  ListFormVersionRestorePointsRequest,
  ListFormVersionRestorePointsResponse,
  ReadFormVersionAtRequest,
  ReadFormVersionAtResponse,
  RestoreFormVersionRequest,
  RestoreFormVersionResponse,
} from '#types';
import { functions } from './firebaseFunctions';

/**
 * Lists the restore point ladder for the named form version, newest first.
 *
 * The server samples a fixed set of moments behind now and answers all of them at
 * once, so the request carries no cursor and no limit and the response has no page
 * to resume from.
 *
 * @param request The form version to sample.
 * @returns The whole ladder, where it ends, the reachable window and live's stamps.
 */
export const listFormVersionRestorePoints = async (
  request: ListFormVersionRestorePointsRequest,
): Promise<ListFormVersionRestorePointsResponse> => {
  const listFormVersionRestorePointsFn = httpsCallable<
    ListFormVersionRestorePointsRequest,
    ListFormVersionRestorePointsResponse
  >(functions, 'listFormVersionRestorePoints');

  const response = await listFormVersionRestorePointsFn(request);

  return response.data;
};

/**
 * Lists the saves lying strictly between two adjacent rows the panel already holds.
 *
 * Every property of the request is required — `earlierCursorMicros` is spelled
 * `null` for the gap below the deepest row rather than left out, because SYD-2445
 * puts an `undefined` on the wire as `null` anyway and the contract says so.
 *
 * @param request The form version, the two cursors bounding the gap, and how many
 *   rows are wanted.
 * @returns The rows in the gap, why the walk stopped, and the reachable window.
 */
export const listFormVersionRestorePointsBetween = async (
  request: ListFormVersionRestorePointsBetweenRequest,
): Promise<ListFormVersionRestorePointsBetweenResponse> => {
  const listFormVersionRestorePointsBetweenFn = httpsCallable<
    ListFormVersionRestorePointsBetweenRequest,
    ListFormVersionRestorePointsBetweenResponse
  >(functions, 'listFormVersionRestorePointsBetween');

  const response = await listFormVersionRestorePointsBetweenFn(request);

  return response.data;
};

/**
 * Reads the named form version as it stood at one restore point.
 *
 * Every property of the request is required, so nothing is spread here: the
 * SYD-2445 hazard is an `undefined` reaching the wire as `null`, and this
 * request has no optional field to leave out.
 *
 * @param request The version to read and the restore point, as its opaque
 *   `cursorMicros`.
 * @returns The outcome, the moment actually read, and the reachable window.
 */
export const readFormVersionAt = async (
  request: ReadFormVersionAtRequest,
): Promise<ReadFormVersionAtResponse> => {
  const readFormVersionAtFn = httpsCallable<ReadFormVersionAtRequest, ReadFormVersionAtResponse>(
    functions,
    'readFormVersionAt',
  );

  const response = await readFormVersionAtFn(request);

  return response.data;
};

/**
 * Restores the named form version, and the rows of every table the request names,
 * to how they stood at one restore point — one transaction covering all of them.
 * Refuses if the live document has moved since `expectedLastModifiedMillis` was
 * read, or if a named table's digests no longer match.
 *
 * Nothing is spread here. `tables` and `skipBlockedRows` are optional on the
 * contract, but `useFormRecoveryRestore` always spells both, so no `undefined`
 * reaches the wire as a `null` the server would read as a choice (SYD-2445).
 *
 * @param request The version to restore, the restore point as its opaque
 *   `cursorMicros`, the baseline the operator's confirmation was shown, the tables
 *   to carry with it, and whether to proceed without the rows it cannot write.
 * @returns The moment actually read, every key the transaction wrote, what each
 *   table restored and skipped, and the stamp it wrote — the next
 *   `expectedLastModifiedMillis`.
 */
export const restoreFormVersionTo = async (
  request: RestoreFormVersionRequest,
): Promise<RestoreFormVersionResponse> => {
  const restoreFormVersionToFn = httpsCallable<
    RestoreFormVersionRequest,
    RestoreFormVersionResponse
  >(functions, 'restoreFormVersionTo');

  const response = await restoreFormVersionToFn(request);

  return response.data;
};
